import React, { memo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { DateTime } from 'luxon'
import { colors } from 'src/theme'
import { DataRow } from 'src/components/shared/DataRow'
import {
  YardstikReport,
  YardstikScreening,
  YardstikScreeningClinic,
  YardstikScreeningDetail,
  YardstikScreeningStatus,
} from 'src/interfaces/collectedData'

const DRUG_SCREEN_NAME = 'DrugScreen'

const humanize = (value: string): string =>
  value
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')

const isDrugScreen = (screening: YardstikScreening): boolean => screening.name === DRUG_SCREEN_NAME

/**
 * The vendor's own status string. The API splits the screening status: `status` holds the
 * normalized value and is only present for persisted screenings, so `providerStatus` is the
 * one that carries the vocabulary this component labels and colours by.
 */
const getScreeningStatus = (screening: YardstikScreening): string =>
  screening.providerStatus ?? screening.status ?? ''

/** Details report their own progress; the first one that has any stands for the screening. */
const getScreeningProgress = (screening: YardstikScreening): string | null => {
  const detail = screening.details?.find((item) => {
    return !!item.progress
  })

  return detail?.progress ?? null
}

const getScreeningStatusLabel = (screening: YardstikScreening): string => {
  const status = getScreeningStatus(screening)

  if (isDrugScreen(screening) && status === YardstikScreeningStatus.Pending) {
    const progress = getScreeningProgress(screening)

    if (progress) {
      return humanize(progress)
    }
  }

  return status === '' ? '-' : humanize(status)
}

const getStatusColor = (status: string): string => {
  const normalized = status.toLowerCase()

  if (normalized === 'clear' || normalized === 'complete' || normalized === 'completed') {
    return colors.COLORS_BLUE_500
  }

  if (
    normalized === 'consider' ||
    normalized === 'suspended' ||
    normalized === 'canceled' ||
    normalized === 'dispute'
  ) {
    return colors.COLORS_RED_500
  }

  return colors.COLORS_ORANGE_500
}

const formatDate = (value: string | null | undefined): string | null => {
  if (!value) {
    return null
  }

  const dateTime = DateTime.fromISO(value)

  return dateTime.isValid ? dateTime.toFormat('MM/dd/yyyy') : null
}

/** A present-but-unparseable date still occupies its row, so it renders as a dash. */
const formatDateOrDash = (value: string | null | undefined): string | null => {
  if (!value) {
    return null
  }

  return formatDate(value) ?? '-'
}

/**
 * `lastActionDate` from the API is completion, else submission, else creation. A drug screen's
 * sample-collection date is more informative than either of the latter two, so it takes priority
 * once the screening has no completion date of its own.
 */
const getLastActionDate = (screening: YardstikScreening): string | null => {
  const sampleCollectedOn = isDrugScreen(screening)
    ? screening.clinic?.sampleCollectedOn
    : undefined
  const value = screening.completedAt ?? sampleCollectedOn ?? screening.lastActionDate

  if (!value) {
    return null
  }

  const dateTime = DateTime.fromISO(value)

  return dateTime.isValid ? dateTime.toFormat('MM/dd/yyyy h:mm a') : null
}

interface ClinicValues {
  clinicName: string | null
  visitWindow: string | null
  sampleCollectedOn: string | null
  sampleReceivedByLabOn: string | null
}

const CLINIC_FIELDS: Array<keyof ClinicValues> = [
  'clinicName',
  'visitWindow',
  'sampleCollectedOn',
  'sampleReceivedByLabOn',
]

const CLINIC_DETAIL_FIELDS: Array<keyof YardstikScreeningDetail> = [
  'clinicName',
  'clinicVisitTimeframeStartsOn',
  'clinicVisitTimeframeEndsOn',
  'sampleCollectedOn',
  'sampleReceivedAtLabOn',
]

/**
 * A screening can carry several details, each with its own clinic and sample dates, so each one
 * is rendered as its own block. A detail's `name` is opaque provider vocabulary, so a detail
 * qualifies by actually carrying clinic data rather than by what the provider named it.
 */
const getClinicDetails = (screening: YardstikScreening): Array<YardstikScreeningDetail> => {
  if (!screening.details?.length) {
    return []
  }

  return screening.details.filter((detail) => {
    return CLINIC_DETAIL_FIELDS.some((field) => {
      return !!detail[field]
    })
  })
}

const buildClinicVisitWindow = (
  startsOn: string | null | undefined,
  endsOn: string | null | undefined,
): string | null => {
  const start = formatDate(startsOn)
  const end = formatDate(endsOn)

  return start && end ? `${start} - ${end}` : null
}

const getDetailClinicValues = (detail: YardstikScreeningDetail): ClinicValues => {
  return {
    clinicName: detail.clinicName ?? null,
    visitWindow: buildClinicVisitWindow(
      detail.clinicVisitTimeframeStartsOn,
      detail.clinicVisitTimeframeEndsOn,
    ),
    sampleCollectedOn: formatDateOrDash(detail.sampleCollectedOn),
    sampleReceivedByLabOn: formatDateOrDash(detail.sampleReceivedAtLabOn),
  }
}

const getScreeningClinicValues = (clinic: YardstikScreeningClinic): ClinicValues => {
  return {
    clinicName: clinic.clinic || null,
    visitWindow: buildClinicVisitWindow(
      clinic.clinicVisitWindowStartsOn,
      clinic.clinicVisitWindowEndsOn,
    ),
    sampleCollectedOn: formatDateOrDash(clinic.sampleCollectedOn),
    sampleReceivedByLabOn: formatDateOrDash(clinic.sampleReceivedByLabOn),
  }
}

/**
 * Detail-level clinic data takes precedence over the screening-level summary: the screening block
 * only contributes the fields that no detail already rendered, so each clinic field is shown at
 * most once while a value coming from just one of the two sources is still preserved.
 */
const getScreeningOnlyClinicValues = (
  screeningValues: ClinicValues,
  detailValues: Array<ClinicValues>,
): ClinicValues => {
  const takeIfNotRendered = (field: keyof ClinicValues): string | null => {
    const renderedByDetail = detailValues.some((values) => {
      return values[field] !== null
    })

    return renderedByDetail ? null : screeningValues[field]
  }

  return {
    clinicName: takeIfNotRendered('clinicName'),
    visitWindow: takeIfNotRendered('visitWindow'),
    sampleCollectedOn: takeIfNotRendered('sampleCollectedOn'),
    sampleReceivedByLabOn: takeIfNotRendered('sampleReceivedByLabOn'),
  }
}

const hasClinicValues = (values: ClinicValues): boolean => {
  return CLINIC_FIELDS.some((field) => {
    return values[field] !== null
  })
}

const renderClinicBlock = (values: ClinicValues, key: string) => {
  if (!hasClinicValues(values)) {
    return null
  }

  return (
    <div key={key}>
      {values.clinicName && <DataRow title="Clinic" text={values.clinicName} />}
      {values.visitWindow && <DataRow title="Clinic Visit Window" text={values.visitWindow} />}
      {values.sampleCollectedOn && (
        <DataRow title="Sample Collected" text={values.sampleCollectedOn} />
      )}
      {values.sampleReceivedByLabOn && (
        <DataRow title="Sample Received by Lab" text={values.sampleReceivedByLabOn} />
      )}
    </div>
  )
}

const renderScreeningRow = (screening: YardstikScreening, index: number) => {
  const label = screening.label || screening.name
  const statusLabel = getScreeningStatusLabel(screening)
  const statusColor = getStatusColor(getScreeningStatus(screening))
  const lastActionDate = getLastActionDate(screening)
  const screeningKey = screening.providerScreeningId ?? String(index)
  const clinicDetails = getClinicDetails(screening)
  const detailClinicValues = clinicDetails.map((detail) => {
    return getDetailClinicValues(detail)
  })
  const screeningClinicValues = screening.clinic
    ? getScreeningOnlyClinicValues(getScreeningClinicValues(screening.clinic), detailClinicValues)
    : null

  return (
    <div key={screeningKey} css={tw`mt-4`}>
      <Typography
        css={tw`mb-1`}
        color={colors.BASICS_BLACK}
        variant="body1"
        style={{ fontSize: 14, fontWeight: 600 }}
      >
        {label}
      </Typography>
      <DataRow title="Last updated" text={lastActionDate ?? '-'} />
      <DataRow
        title="Status"
        component={
          <Typography variant="body2" style={{ color: statusColor }}>
            {statusLabel}
          </Typography>
        }
      />
      {detailClinicValues.map((values, detailIndex) => {
        return renderClinicBlock(
          values,
          clinicDetails[detailIndex].providerDetailId ?? String(detailIndex),
        )
      })}
      {screeningClinicValues && renderClinicBlock(screeningClinicValues, `${screeningKey}-clinic`)}
    </div>
  )
}

interface Props {
  yardstikReport: YardstikReport
}

const YardstikScreeningsComponent = ({ yardstikReport }: Props) => {
  if (!yardstikReport.screenings?.length) {
    return null
  }

  return <div>{yardstikReport.screenings.map(renderScreeningRow)}</div>
}

export const YardstikScreenings = memo(YardstikScreeningsComponent)
